import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Divider,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Chip,
  Stack,
  Tooltip
} from "@mui/material";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ErrorIcon from "@mui/icons-material/Error";
import { formatNumber } from "../../functions/misc";
import { ReferralListTableProps, TransformedLead, TabType } from "./types";
import { CustomPaginationActions } from "./CustomPaginationActions";
import Footer from "../footer";

interface StatusChipProps {
  status: string;
}

const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const getStatusStyles = () => {
    switch (status) {
      case "Enrolled":
        return { label: "Enrolled", bgColor: "#15b79f", textColor: "#fff" };
      case "Pending":
        return { label: "Pending", bgColor: "#ffeb3b", textColor: "#000" };
      case "PaymentHold":
        return { label: "Payment Issue", bgColor: "#ffc107", textColor: "#000" };
      case "Canceled":
        return { label: "Cancelled", bgColor: "#dc3545", textColor: "#fff" };
      default:
        return { label: status, bgColor: "#2196f3", textColor: "#fff" };
    }
  };

  const { label, bgColor, textColor } = getStatusStyles();

  return (
    <Chip
      label={label}
      sx={{
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 500,
        fontSize: "0.75rem",
        width: "auto !important",
      }}
      size="small"
    />
  );
};

interface AlertLevelProps {
  level: string;
}

const AlertLevel: React.FC<AlertLevelProps> = ({ level }) => {
  const getAlertColor = () => {
    switch (level) {
      case 'LOW': return '#0E9382';
      case 'MEDIUM': return '#FB9C0C';
      case 'HIGH': return '#DE3024';
      default: return '#000';
    }
  };

  return (
    <Typography
      variant="body2"
      sx={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 500,
        color: getAlertColor(),
      }}
    >
      {level}
    </Typography>
  );
};

const chipStyles = {
  color: "#212636",
  fontWeight: 500,
  fontSize: "13px",
  whiteSpace: "nowrap",
  alignSelf: "flex-start",
  width: "auto !important",
};

const ReferralListTable: React.FC<ReferralListTableProps> = ({
  tabs = ["All", "Pending", "Enrolled", "Payment Issue", "Cancelled"],
  defaultTab = "All",
  data = [],
  loading = false,
  totalCount = 0,
  columnConfig = [],
  onTabChange,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onSortChange,
  onRowClick,
  detailComponent: DetailComponent,
  nextToken,
  actions = [],
  currentSorting = { key: "ReferredAt", direction: "descending" },
  currentPage: propCurrentPage = 0,
  rowsPerPage: propRowsPerPage = 10,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabType>(defaultTab as TabType);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TransformedLead | null>(null);

  const currentPage = propCurrentPage;
  const rowsPerPage = propRowsPerPage;
  const sorting = currentSorting;

  useEffect(() => {
    if (showModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showModal]);

  const skeletonRows = useMemo(() =>
    Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {columnConfig.map((column, colIndex) => (
          <TableCell key={`skeleton-cell-${colIndex}`}>
            <Skeleton animation="wave" height={colIndex === 0 ? 42 : 24} />
          </TableCell>
        ))}
      </TableRow>
    )),
  [rowsPerPage, columnConfig]);

  const actionButtons = useMemo(() =>
    actions.map((action, index) => (
      <Button
        key={`action-${index}`}
        variant={action.variant || "outlined"}
        color={action.color || "primary"}
        size="small"
        startIcon={action.icon}
        onClick={action.onClick}
        sx={{ mr: 1 }}
      >
        {action.label}
      </Button>
    )),
  [actions]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    const maxPage = Math.ceil(totalCount / rowsPerPage);

    if (newPage <= maxPage && newPage >= 0) {
      onPageChange?.(newPage);
    } else {
      console.warn("Blocked page change: Out of range");
    }
  }, [onPageChange, rowsPerPage, totalCount]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    onRowsPerPageChange?.(newRowsPerPage);
  }, [onRowsPerPageChange]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newTab: TabType) => {
    setSelectedTab(newTab);
    onTabChange?.(newTab);
  }, [onTabChange]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if(value.length > 2 || value.length === 0){
      onSearch?.(value);
    }
  }, [onSearch]);

  const handleSortChange = useCallback((columnKey: string) => {
    const newDirection = sorting.key === columnKey && sorting.direction === "ascending" ? "descending" : "ascending";
    onSortChange?.(columnKey, newDirection);
  }, [onSortChange, sorting]);

  const handleRowClick = useCallback((row: TransformedLead) => {
    if (onRowClick) {
      onRowClick(row);
    } else if (DetailComponent) {
      setSelectedRow(row);
      setShowModal(true);
    }
  }, [onRowClick, DetailComponent]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedRow(null);
  }, []);

  const renderCellContent = useCallback((row: TransformedLead, column: { key: string; type: string; render?: (row: TransformedLead) => React.ReactNode }) => {
    const value = row[column.key];

    if (column.render) {
      return column.render(row);
    }

    switch (column.type) {
      case "date":
        return value ? moment(value).format("MM/DD/YYYY") : "-";

      case "email":
        return <Typography variant="body2" color="textSecondary">{value}</Typography>;

      case "status":
        return <StatusChip status={value} />;

      case "alertLevel":
        return <AlertLevel level={value} />;

      case "name":
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {row.hasAlert && (
              <Tooltip title="Alert" arrow>
                <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
              </Tooltip>
            )}
            <Box>
              <Typography
                variant="body2"
                sx={{ cursor: "pointer", color: "#2968F1", textDecoration: "underline" }}
                className="lead-email"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(row);
                }}
              >
                {value}
              </Typography>
              {row.email && (
                <Typography variant="caption" color="#667085" className="lead-email">
                  {row.email}
                </Typography>
              )}
            </Box>
          </Box>
        );

      case "services":
        return (
          <Stack direction="column" spacing={0.5}>
            {(value as string[])?.map((service, index) => (
              <Chip
                key={`service-${index}`}
                label={service}
                variant="filled"
                size="small"
                sx={{
                  ...chipStyles,
                  backgroundColor: "#F1F1F4",
                }}
              />
            ))}
          </Stack>
        );

      case "plan":
        return value && value.length > 0 ?
        (
          <Chip
            label={value}
            variant="outlined"
            size="small"
            sx={chipStyles}
          />
        ) :
        (
          <span></span>
        );

      default:
        return (
          <Typography
            variant="body2"
            sx={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {value}
          </Typography>
        );
    }
  }, [handleRowClick]);

  const emptyState = useMemo(() => (
    <TableRow>
      <TableCell colSpan={columnConfig.length} align="center" sx={{ py: 3 }}>
        <Typography variant="body1" color="textSecondary" align="center">
          No records found
        </Typography>
      </TableCell>
    </TableRow>
  ), [columnConfig.length]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="referral status tabs"
            sx={{ minHeight: 48 }}
          >
            {tabs.map((tab) => (
              <Tab
                key={`tab-${tab}`}
                label={tab}
                value={tab}
                id={`tab-${tab}`}
                aria-controls={`tabpanel-${tab}`}
                sx={{ minHeight: 48, textTransform: "none" }}
              />
            ))}
          </Tabs>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ px: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }} gap={2}>
          {actions.length > 0 && (
            <Box>{actionButtons}</Box>
          )}

          <TextField
            placeholder="Search Client Names"
            size="small"
            type="search"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
            aria-label="Search clients"
          />
        </Box>

        <TableContainer>
          <Table size="medium" aria-label="referral list">
            <TableHead>
              <TableRow>
                {columnConfig.map((column) => (
                  <TableCell
                    key={`header-${column.key}`}
                    align={column.align || "left"}
                    sx={{
                      fontWeight: "bold",
                      cursor: column.sortable ? "pointer" : "default",
                      whiteSpace: "nowrap"
                    }}
                    onClick={() => column.sortable && handleSortChange(column.field)}
                    aria-sort={column.sortable ? (sorting.key === column.field ? sorting.direction : "none") : undefined}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {column.label}
                      {sorting.key === column.field && (
                        <Box component="span" sx={{ ml: 0.5 }} aria-hidden="true">
                          {sorting.direction === "ascending" ? (
                            <ArrowUpwardIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardIcon fontSize="small" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? skeletonRows
                : data.length === 0
                  ? emptyState
                  : data.map((row, index) => (
                      <TableRow
                        key={row.id || `row-${index}`}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          cursor: onRowClick || DetailComponent ? 'pointer' : 'default'
                        }}
                        aria-label={`Referral: ${row.name || row.email || index}`}
                      >
                        {columnConfig.map((column) => (
                          <TableCell
                            key={`${row.id || index}-${column.key}`}
                            align={column.align || "left"}
                            sx={{
                              whiteSpace: column.wrap === false ? "nowrap" : "normal",
                              verticalAlign: "top"
                            }}
                          >
                            {renderCellContent(row, column)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
              }
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 2, borderTop: "1px solid rgba(224, 224, 224, 1)" }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                padding: 0,
              },
              ".MuiTablePagination-selectLabel": {
                margin: 0,
              },
              ".MuiTablePagination-displayedRows": {
                margin: 0,
              },
            }}
            labelDisplayedRows={({ from, to, count }) => {
              const formattedTotal = formatNumber(count);
              return `${from}–${to} of ${formattedTotal}`;
            }}
            ActionsComponent={(subProps) => (
              <CustomPaginationActions
                {...subProps}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
              />
            )}
          />
        </Box>
      </Paper>

      {DetailComponent && (
        <Dialog
          open={showModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
          aria-labelledby="client-detail-dialog-title"
        >
          <DialogTitle id="client-detail-dialog-title" sx={{ m: 0, p: 2 }}>
            Client Information
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers className="detail-content">
            <DetailComponent lead={selectedRow?.originalData} />
            <Footer/>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ReferralListTable;